// Import LitElement and Splide
import { LitElement, html, css } from 'lit';
import Splide from '@splidejs/splide';

/**
 * Class representing a custom slider element based on LitElement and Splide.
 * @extends LitElement
 */
export class SkrSplide extends LitElement {
    static get properties() {
        return {
            slidesToShow: { type: Number, attribute: 'slides-to-show', reflect: true },
            slidesPerMove: { type: Number, attribute: 'slides-per-move', reflect: true },
            sliderType: { type: String, attribute: 'slider-type', reflect: true },
            sliderGap: { type: String, attribute: 'slider-gap', reflect: true },
            startIndex: { type: String, attribute: 'slider-start-index', reflect: true },
            sliderAspectRatio: { type: String, attribute: 'slider-aspect-ratio', reflect: true },
            sliderBreakpoints: { type: String, attribute: 'slider-breakpoints', reflect: true },
            slidesFixedWidth: { type: String, attribute: 'slides-fixed-width', reflect: true },
            slidesFixedHeight: { type: String, attribute: 'slides-fixed-height', reflect: true },
            sliderShowPagination: { type: Boolean, attribute: 'slider-show-pagination', reflect: true },
            sliderRewind: { type: Boolean, attribute: 'slider-rewind', reflect: true },
            sliderFocus: { type: String, attribute: 'slider-focus', reflect: true },
            keyboardListeners: { type: String, attribute: 'keyboard-listeners', reflect: true },
            splideContainer: { type: Object },
            isNavigation: { type: Boolean, attribute: 'is-navigation' },
            syncId: { type: String, attribute: 'sync-id' },
            isMaster: { type: Boolean, attribute: 'is-master' },
            isSynced: { type: Boolean, attribute: 'is-synced' },
        }
    }

    constructor() {
        super();

        this.slidesToShow = 1;
        this.slidesPerMove = 1;
        this.sliderType = 'slide';
        this.sliderGap = null;
        this.startIndex = null;
        this.sliderAspectRatio = null;
        this.sliderBreakpoints = null;
        this.sliderFixedWidth = null;
        this.sliderFixedHeight = null;
        this.sliderShowPagination = false;
        this.sliderFocus = 'center';
        this.sliderRewind = false;
        this.isNavigation = false;
        this.keyboardListeners = 'false';
        this.syncId = null;
        this.isMaster = null;
        this.isSynced = false;

        this._handleSliderMount = this._handleSliderMount.bind(this);
        this._handleSliderUpdated = this._handleSliderUpdated.bind(this);
        this._handlePaginationUpdate = this._handlePaginationUpdate.bind(this);
        this._onMasterSliderMount = this._onMasterSliderMount.bind(this);

        window.addEventListener('skr-slider-mounted', this._onMasterSliderMount);
    }

    /**
     * Creates a render root for the LitElement so the element is only a wrapper without rendering any content.
     * @returns {LitElement} The element itself.
     */
    createRenderRoot() {
        return this;
    }

    firstUpdated() {
        this.splideContainer = this.querySelector('.splide') || null;

        if (!this.splideContainer) {
            console.error("SKR: Could not get necessary elements to initialize the slider");
            return;
        } else {
            this._initSlider();
        }
    }

    /**
     * Initializes the Splide slider with the specified options.
     * @private
     */
    _initSlider() {
        let sliderOptions = {
            classes: {
                pagination: 'splide__pagination slider-bullets',
                page: 'splide__pagination__page slider-bullets__bullet',
            },
            type: this.sliderType,
            reducedMotion: {
                speed: 0,
                rewindSpeed: 0,
                autoplay: 'pause'
            },
            focusableNodes: '*',
            focus: (this.sliderFocus === 'center' ? 'center' : parseInt(String(this.sliderFocus), 10)),
            pagination: this.sliderShowPagination,
            rewind: this.sliderRewind,
            isNavigation: this.isNavigation,
            start: this.startIndex ? parseInt(this.startIndex) : 0,
            keyboard: this.keyboardListeners,
            mediaQuery: 'min',
            trimSpace: true,
        }

        if (this.slidesToShow) {
            sliderOptions.perPage = parseInt(String(this.slidesToShow));
        }

        if (this.slidesPerMove) {
            sliderOptions.perMove = parseInt(String(this.slidesPerMove));
        }

        if (this.sliderAspectRatio) {
            sliderOptions.heightRatio = this.sliderAspectRatio;
        }

        if (this.sliderGap) {
            sliderOptions.gap = parseInt(String(this.sliderGap));
        }

        if (this.slidesFixedHeight) {
            sliderOptions.fixedHeight = parseInt(String(this.slidesFixedHeight));
        }

        if (this.slidesFixedWidth) {
            sliderOptions.fixedWidth = parseInt(String(this.slidesFixedWidth));
        }

        if (this.sliderBreakpoints) {
            sliderOptions.breakpoints = JSON.parse(this.sliderBreakpoints);
        }

        this.splideSlider = new Splide(this.splideContainer, sliderOptions);

        this.splideSlider.on('mounted', this._handleSliderMount);
        this.splideSlider.on('moved', this._handlePaginationUpdate);
        this.splideSlider.on('refresh', this._handleSliderUpdated);

        if (this.isMaster) {
            this.splideSlider.mount();
        }
    }

    /**
     * Event handler for when the slider is moved.
     * @param {number} event - The index of the moved slide.
     * @private
     */
    _handlePaginationUpdate(event) {
        this._activeSlideIndex = event + 1;

        this.dispatchEvent(new CustomEvent('skr-slider-moved', {
            bubbles: true,
            composed: true,
            detail: {
                el: this.splideSlider,
                activeIndex: this._activeSlideIndex
            }
        }));
    }

    _handleSliderUpdated() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('skr-slider-refresh', {
                bubbles: true,
                composed: true,
                detail: {
                    container: this.splideContainer,
                    slider: this.splideSlider,
                    activeIndex: this._activeSlideIndex
                }
            }));
        })
    }

    /**
     * Event handler for when the slider has mounted.
     * @private
     */
    _handleSliderMount() {
        this._removeSlidesTabpanelRole();

        this.dispatchEvent(new CustomEvent('skr-slider-mounted', {
            bubbles: true,
            composed: true,
            detail: {
                el: this.splideSlider
            }
        }));

        // Hack to ensure the main slide correctly syncs to the master
        setTimeout(() => {
            this.splideSlider.refresh();
        }, 100);
    }

    /**
     * Removes the role attributes from slides that have 'tabpanel' role to improve a11y
     * @private
     */
    _removeSlidesTabpanelRole() {
        const elsWithPanelTabRole = this.splideSlider.root.querySelectorAll('*[role="tabpanel"]');
        if (!elsWithPanelTabRole) return;

        elsWithPanelTabRole.forEach((el) => {
            el.removeAttribute('role');
            el.removeAttribute('aria-label');
        });
    }

    /**
     * Syncs with another slider when a master slider is mounted.
     * @param {CustomEvent} event - The event containing the foreign slider details.
     * @private
     */
    _onMasterSliderMount(event) {
        if (event.detail.el === this.splideSlider || this.isMaster || this.isSynced) {
            return;
        }

        this.splideSlider.sync(event.detail.el);
        this.splideSlider.mount();
        this.isSynced = true;
    }
}

customElements.define('skr-splide', SkrSplide);
